import { useState, useEffect } from 'react';
import { AlertCircle, ChevronRight } from 'lucide-react';
import { ACCENT, WARN, BAD } from '../../lib/uiTokens';
import MesicniGraf from '../finance/MesicniGraf';

const sklonuj = (n, [j, dva, vic]) => `${n} ${n === 1 ? j : (n >= 2 && n <= 4 ? dva : vic)}`;

const fmt = (v) => (v == null ? '—' : new Intl.NumberFormat('cs-CZ', {
    minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(v));

/**
 * Pravý panel statistik dokladů — výdaje za období, ke schválení / čeká na platbu,
 * rozpad dle P&L kategorie (bary) a měsíční výdaje. Vyplní výšku (flex-col h-full),
 * „Podle kategorie" roste a scrolluje uvnitř. Zobrazuje se, když není otevřený detail.
 *
 * Nahoře sedí „Vyžaduje pozornost" — co po člověku systém chce, než začne číst
 * čísla. Dřív to byl pruh nad seznamem a ukrajoval mu dva řádky pokaždé, když
 * něco viselo. Tady je na to místo i na celou větu; v seznamu zbyl barevný
 * počet v pilulce, který tutéž věc řekne bez jediného pixelu navíc.
 *
 * @param {{blokuje?: number, po_splatnosti?: number}} diagnostiky  počty ze seznamu
 * @param {(id: string) => void} onFiltr  přepne pohled v seznamu
 */
export default function DokladyStatistikyPanel({ rok, refreshKey, cardStyle, diagnostiky = {}, onFiltr = null }) {
    const [staty, setStaty] = useState(null);

    // Roční přehled (bez měsíce) — graf pak ukazuje celý rok, KPI jsou roční součty.
    useEffect(() => {
        const p = new URLSearchParams();
        if (rok) p.set('rok', rok);
        fetch('/api/finance/doklady/statistiky?' + p.toString(), { credentials: 'same-origin' })
            .then(r => r.json()).then(setStaty).catch(() => {});
    }, [rok, refreshKey]);

    const ukoly = [
        {
            id: 'blokuje', pocet: diagnostiky.blokuje ?? 0, barva: BAD,
            text: sklonuj(diagnostiky.blokuje ?? 0, ['doklad nejde schválit', 'doklady nejdou schválit', 'dokladů nejde schválit']),
            proc: 'Chybí dodavatel, nebo položka nemá P&L účet či vazbu do katalogu.',
        },
        {
            id: 'po_splatnosti', pocet: diagnostiky.po_splatnosti ?? 0, barva: WARN,
            text: `${sklonuj(diagnostiky.po_splatnosti ?? 0, ['doklad je', 'doklady jsou', 'dokladů je'])} po splatnosti`,
            proc: 'Splatnost už uplynula a doklad není zaplacený.',
        },
    ].filter(u => u.pocet > 0);

    // Když není co řešit, karta se nekreslí. Ticho je taky informace a nula
    // v rámečku by ji přebila.
    const Pozornost = () => (ukoly.length === 0 ? null : (
        <div className="rounded-2xl overflow-hidden flex-shrink-0" style={cardStyle}>
            <div className="text-xs uppercase px-4 pt-4 pb-2" style={{ color: 'var(--text-muted)' }}>Vyžaduje pozornost</div>
            {ukoly.map((u, i) => (
                <button key={u.id} type="button" onClick={() => onFiltr?.(u.id)} disabled={!onFiltr}
                    className="w-full text-left flex items-start gap-2.5 px-4 py-2.5"
                    style={{
                        borderTop: i === 0 ? 'none' : '1px solid var(--border-default)',
                        background: 'transparent', cursor: onFiltr ? 'pointer' : 'default',
                        WebkitTapHighlightColor: 'transparent',
                    }}>
                    <AlertCircle size={15} style={{ color: u.barva, flexShrink: 0, marginTop: 1 }} />
                    <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-semibold" style={{ color: u.barva }}>{u.text}</span>
                        <span className="block text-[11.5px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{u.proc}</span>
                    </span>
                    {onFiltr && <ChevronRight size={15} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 1 }} />}
                </button>
            ))}
        </div>
    ));

    // Diagnostiky chodí ze seznamu, ne z `/statistiky` — ukážou se i dokud se
    // čísla panelu načítají, protože jsou to právě ona „co teď", kvůli kterým
    // se do Dokladů chodí.
    if (!staty) {
        return (
            <div className="flex flex-col gap-3 h-full">
                <Pozornost />
                <div className="rounded-2xl p-4 text-sm" style={{ ...cardStyle, color: 'var(--text-muted)' }}>Načítám statistiky…</div>
            </div>
        );
    }

    const kat = staty.podle_kategorie || [];
    const maxK = Math.max(1, ...kat.map(k => k.suma));
    const mesicne = staty.mesicne || [];
    const Karta = ({ children, className = '' }) => <div className={`rounded-2xl p-4 ${className}`} style={cardStyle}>{children}</div>;

    return (
        <div className="flex flex-col gap-3 h-full">
            <Pozornost />

            <Karta>
                <div className="text-xs uppercase mb-1" style={{ color: 'var(--text-muted)' }}>Výdaje (doklady)</div>
                <div className="text-3xl font-bold tabular-nums" style={{ color: 'var(--text)' }}>{fmt(staty.vydaje)} Kč</div>
                <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{staty.pocet} dokladů za rok {rok}</div>
            </Karta>

            <div className="grid grid-cols-2 gap-3 flex-shrink-0">
                <Karta>
                    <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Ke schválení</div>
                    <div className="text-2xl font-bold tabular-nums" style={{ color: staty.ke_schvaleni > 0 ? WARN : 'var(--text)' }}>{staty.ke_schvaleni}</div>
                </Karta>
                <Karta>
                    <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Čeká na platbu</div>
                    <div className="text-2xl font-bold tabular-nums" style={{ color: 'var(--text)' }}>{staty.ceka_na_platbu_pocet}</div>
                    <div className="text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>{fmt(staty.ceka_na_platbu_suma)} Kč</div>
                </Karta>
            </div>

            {kat.length > 0 && (
                <Karta className="flex-1 min-h-0 flex flex-col">
                    <div className="text-xs uppercase mb-3 flex-shrink-0" style={{ color: 'var(--text-muted)' }}>Podle kategorie</div>
                    <div className="space-y-3 overflow-y-auto min-h-0" style={{ flex: 1 }}>
                        {kat.map((k, i) => (
                            <div key={i}>
                                <div className="flex items-center justify-between text-xs mb-1">
                                    <span className="truncate" style={{ color: 'var(--text)' }}>{k.nazev}</span>
                                    <span className="tabular-nums ml-2 whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>{fmt(k.suma)} · {k.pocet}×</span>
                                </div>
                                <div style={{ height: 8, borderRadius: 3, background: 'var(--bg-elevated)' }}>
                                    <div style={{ width: `${Math.max(3, k.suma / maxK * 100)}%`, height: 8, borderRadius: 3, background: ACCENT }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </Karta>
            )}

            {mesicne.length > 0 && (
                <Karta>
                    <div className="text-xs uppercase mb-3" style={{ color: 'var(--text-muted)' }}>Měsíční výdaje (celý rok)</div>
                    <MesicniGraf data={mesicne} rok={rok} series={[{ key: 'vydaje', label: 'Výdaje', color: ACCENT }]} />
                </Karta>
            )}
        </div>
    );
}
